import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { temPapel, useAuth } from "../../lib/auth";
import { supabase } from "../../lib/supabase";
import { useTempoReal } from "../../lib/tempoReal";
import { mensagemDeErro, novaChave, rpc, type RespostaWms } from "../../lib/rpc";
import { produtoPorCodigo } from "../../lib/leitura";
import { sinalConcluido, sinalErro, sinalOk } from "../../lib/sinais";
import { fmtDataHora, fmtNum, nomeTipoLocal } from "../../lib/formato";
import { LeituraCodigo } from "../../componentes/LeituraCodigo";
import { AlertaErro, Confirmar } from "../../componentes/Alerta";
import { Botao, Carregando, Selo } from "../../componentes/ui";

interface Tarefa {
  id: string; status: string; prioridade: number; motivo: string | null; qtd_solicitada: number; qtd_movida: number;
  criado_em: string; origem: string; origem_tipo: string; destino: string; destino_tipo: string;
  produto: { id: string; sku: string | null; nome: string }; atribuida_a: string | null;
}
interface Detalhe extends RespostaWms { tarefa: Tarefa; pendentes: number }
interface LinhaFila {
  id: string; status: string; prioridade: number; motivo: string | null; qtd_solicitada: number; criado_em: string;
  origem: string; destino: string; destino_tipo: string; sku: string | null; nome: string; atribuida_a: string | null;
}

type Etapa = "origem" | "produto" | "quantidade" | "destino";

/** Reposição na coletora: pega a tarefa, tira da origem e guarda no destino. */
export default function Reposicao() {
  const { perfil } = useAuth();
  const lider = temPapel(perfil, "lider");
  const [atual, setAtual] = useState<Detalhe | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [aviso, setAviso] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const fila = useQuery({
    queryKey: ["reposicao-fila", perfil?.deposito_padrao_id],
    queryFn: async () => {
      let q = supabase.from("v_reposicao_tarefas").select("*").order("prioridade", { ascending: false }).order("criado_em").limit(50);
      if (perfil?.deposito_padrao_id) q = q.eq("deposito_id", perfil.deposito_padrao_id);
      const { data, error } = await q;
      if (error) throw error;
      return data as LinhaFila[];
    },
    refetchInterval: 20_000,
  });
  useTempoReal(["tarefas_reposicao"], [["reposicao-fila"]]);

  // retoma a tarefa que já está com o estoquista
  const retomar = useCallback(async () => {
    try {
      const { data } = await supabase.from("tarefas_reposicao").select("id").eq("status", "em_andamento").eq("atribuida_a", perfil!.id).limit(1);
      if (data?.length) {
        const r = await rpc<Detalhe>("wms_reposicao_proxima", { p_tarefa_id: data[0].id });
        if (r.ok) setAtual(r);
      }
    } finally {
      setCarregando(false);
    }
  }, [perfil]);

  useEffect(() => { void retomar(); }, [retomar]);

  async function pegar(tarefaId?: string) {
    setAviso(null);
    try {
      const r = await rpc<Detalhe>("wms_reposicao_proxima", { p_tarefa_id: tarefaId ?? null, p_deposito_id: perfil?.deposito_padrao_id ?? null });
      if (r.ok) { sinalOk(); setAtual(r); }
      else setAviso(r.mensagem ?? "Nenhuma reposição disponível");
    } catch (e) {
      setErro(mensagemDeErro(e));
    }
    void fila.refetch();
  }

  async function gerar() {
    try {
      const r = await rpc("wms_reposicao_gerar", { p_deposito_id: perfil?.deposito_padrao_id ?? null });
      setAviso(r.ok ? `${String(r.criadas ?? r.tarefas ?? 0)} reposição(ões) gerada(s)` : r.mensagem ?? "Nada gerado");
    } catch (e) {
      setErro(mensagemDeErro(e));
    }
    void fila.refetch();
  }

  async function cancelar(id: string) {
    try {
      await rpc("wms_reposicao_cancelar", { p_tarefa_id: id });
    } catch (e) {
      setErro(mensagemDeErro(e));
    }
    void fila.refetch();
  }

  if (carregando) return <Carregando />;

  if (atual) {
    return <Execucao detalhe={atual} onFim={(msg) => { setAtual(null); setAviso(msg); void fila.refetch(); }} />;
  }

  const pendentes = (fila.data ?? []).filter((t) => t.status === "pendente");
  const andamento = (fila.data ?? []).filter((t) => t.status === "em_andamento");

  return (
    <div className="flex flex-col gap-4">
      <AlertaErro mensagem={erro} onFechar={() => setErro(null)} />
      <h1 className="font-sinal text-3xl font-bold">Reposição</h1>
      {aviso && <p className="rounded-md bg-info-claro px-3 py-3 text-lg font-semibold text-info" role="status">{aviso}</p>}
      <Botao grande className="min-h-24 text-2xl" disabled={!pendentes.length} onClick={() => void pegar()}>
        {pendentes.length ? `Pegar próxima (${pendentes.length} na fila)` : "Nenhuma reposição pendente"}
      </Botao>
      {lider && <Botao variante="secundario" onClick={() => void gerar()}>Gerar reposições abaixo do mínimo agora</Botao>}

      {pendentes.length > 0 && (
        <section className="rounded-lg border-2 border-aco bg-chapa">
          <h2 className="border-b-2 border-aco px-4 py-3 font-sinal text-xl font-semibold">Fila</h2>
          <ul className="divide-y divide-aco">
            {pendentes.map((t) => (
              <li key={t.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                <div className="min-w-0">
                  <p className="font-sinal text-xl font-bold">{t.origem} → {t.destino}</p>
                  <p>{fmtNum(t.qtd_solicitada)} × {t.sku} {t.nome}</p>
                  {t.motivo && <p className="text-sm text-aco-escuro">{t.motivo}</p>}
                </div>
                <div className="flex items-center gap-2">
                  {t.prioridade >= 30 && <Selo tom="faixa">Urgente</Selo>}
                  <Botao variante="secundario" onClick={() => void pegar(t.id)}>Pegar</Botao>
                  {lider && <Botao variante="fantasma" onClick={() => void cancelar(t.id)}>Cancelar</Botao>}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
      {andamento.length > 0 && (
        <section className="rounded-lg border-2 border-aco bg-chapa p-4">
          <h2 className="font-sinal text-lg font-semibold">Em andamento com outras pessoas</h2>
          <ul className="mt-2 flex flex-col gap-1">
            {andamento.map((t) => <li key={t.id}>{t.origem} → {t.destino}: {t.sku} com {t.atribuida_a ?? "?"}</li>)}
          </ul>
        </section>
      )}
      <Link to="/" className="text-center font-semibold underline">Voltar ao início</Link>
    </div>
  );
}

function Execucao({ detalhe, onFim }: { detalhe: Detalhe; onFim: (mensagem: string | null) => void }) {
  const t = detalhe.tarefa;
  const [etapa, setEtapa] = useState<Etapa>("origem");
  const [origem, setOrigem] = useState("");
  const [produto, setProduto] = useState("");
  const [qtd, setQtd] = useState(t.qtd_solicitada);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [lampejo, setLampejo] = useState(0);
  const [tremor, setTremor] = useState(0);
  const [confirmar, setConfirmar] = useState<"falta" | "liberar" | null>(null);

  function falhou(m: string) {
    sinalErro();
    setTremor((n) => n + 1);
    setErro(m);
  }
  function avancar(proxima: Etapa) {
    sinalOk();
    setLampejo((n) => n + 1);
    setEtapa(proxima);
  }

  async function leitura(codigo: string) {
    if (ocupado) return;
    if (etapa === "origem") {
      if (codigo.trim().toUpperCase() !== t.origem) return falhou(`Origem errada. Pegue em ${t.origem}`);
      setOrigem(codigo);
      return avancar("produto");
    }
    if (etapa === "produto") {
      const p = await produtoPorCodigo(codigo);
      if (!p) return falhou("Código não cadastrado");
      if (p.id !== t.produto.id) return falhou(`Produto errado. Esta reposição é de ${t.produto.sku ?? t.produto.nome}`);
      setProduto(codigo);
      return avancar("quantidade");
    }
    if (etapa === "destino") {
      if (codigo.trim().toUpperCase() !== t.destino) return falhou(`Destino errado. Guarde em ${t.destino}`);
      setOcupado(true);
      try {
        const r = await rpc<Detalhe>("wms_reposicao_concluir", {
          p_tarefa_id: t.id, p_codigo_origem: origem, p_codigo_produto: produto, p_codigo_destino: codigo,
          p_quantidade: qtd, p_idem: novaChave(),
        });
        if (!r.ok) return falhou(r.mensagem ?? "Reposição recusada");
        sinalConcluido();
        onFim(`${fmtNum(qtd)} × ${t.produto.sku ?? t.produto.nome} guardados em ${t.destino}`);
      } catch (e) {
        falhou(mensagemDeErro(e));
      } finally {
        setOcupado(false);
      }
    }
  }

  async function executar(acao: "falta" | "liberar") {
    setOcupado(true);
    try {
      const r = acao === "falta"
        ? await rpc("wms_reposicao_falta", { p_tarefa_id: t.id, p_idem: novaChave() })
        : await rpc("wms_reposicao_liberar", { p_tarefa_id: t.id });
      if (!r.ok) return falhou(r.mensagem ?? "Ação recusada");
      onFim(acao === "falta"
        ? `Falta registrada em ${t.origem}. A posição vai para inventário${r.nova_tarefa ? " e outra origem foi escolhida" : ""}.`
        : "Reposição devolvida para a fila");
    } catch (e) {
      falhou(mensagemDeErro(e));
    } finally {
      setOcupado(false);
      setConfirmar(null);
    }
  }

  const passos: Array<{ id: Etapa; texto: string }> = [
    { id: "origem", texto: "Origem" }, { id: "produto", texto: "Produto" }, { id: "quantidade", texto: "Quantidade" }, { id: "destino", texto: "Destino" },
  ];
  const indice = passos.findIndex((p) => p.id === etapa);

  return (
    <div className="flex flex-col gap-3 pb-4">
      <AlertaErro mensagem={erro} onFechar={() => setErro(null)} />
      <div className="flex items-center gap-2 rounded-lg bg-tinta px-3 py-2 text-white">
        <span className="font-sinal text-xl font-bold">Reposição</span>
        {t.prioridade >= 30 && <Selo tom="faixa">Urgente</Selo>}
        <span className="ml-auto text-sm opacity-80">{detalhe.pendentes} na fila</span>
      </div>
      <ol className="grid grid-cols-4 gap-1" aria-label="Passos">
        {passos.map((p, i) => (
          <li key={p.id} className={`rounded-md px-2 py-2 text-center text-sm font-semibold ${i < indice ? "bg-ok-claro text-ok" : i === indice ? "bg-faixa" : "bg-concreto-escuro text-aco-escuro"}`}>
            {p.texto}
          </li>
        ))}
      </ol>

      <section className="rounded-lg border-2 border-tinta bg-chapa">
        <div className="grid grid-cols-2 divide-x-2 divide-aco">
          <div className={`p-4 ${etapa === "origem" ? "bg-faixa-clara" : ""}`}>
            <p className="text-sm font-semibold text-aco-escuro">Tire de ({nomeTipoLocal[t.origem_tipo] ?? t.origem_tipo})</p>
            <p className="font-sinal text-5xl font-bold leading-none">{t.origem}</p>
          </div>
          <div className={`p-4 ${etapa === "destino" ? "bg-faixa-clara" : ""}`}>
            <p className="text-sm font-semibold text-aco-escuro">Guarde em ({nomeTipoLocal[t.destino_tipo] ?? t.destino_tipo})</p>
            <p className="font-sinal text-5xl font-bold leading-none">{t.destino}</p>
          </div>
        </div>
        <div className="flex items-end justify-between gap-3 border-t-2 border-aco px-4 py-4">
          <div>
            <p className="text-2xl font-semibold leading-tight">{t.produto.nome}</p>
            <p className="text-lg text-aco-escuro">{t.produto.sku}</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold text-aco-escuro">Leve</p>
            <p className="font-sinal text-6xl font-bold leading-none">{fmtNum(etapa === "destino" ? qtd : t.qtd_solicitada)}</p>
          </div>
        </div>
        {t.motivo && <p className="border-t border-aco px-4 py-2 text-sm text-aco-escuro">{t.motivo} · criada {fmtDataHora(t.criado_em)}</p>}
      </section>

      {etapa === "quantidade" ? (
        <section data-dialogo-aberto className="rounded-lg bg-faixa p-4">
          <p className="font-sinal text-2xl font-bold">Quantas unidades você pegou?</p>
          <div className="mt-3 flex items-center gap-3">
            <button type="button" onClick={() => setQtd((q) => Math.max(1, q - 1))} aria-label="Menos um"
              className="min-h-16 w-16 rounded-md border-2 border-tinta bg-chapa text-3xl font-bold">-</button>
            <input type="number" inputMode="numeric" min={1} max={t.qtd_solicitada} value={qtd} aria-label="Quantidade"
              onChange={(e) => setQtd(Math.max(1, Math.min(t.qtd_solicitada, Number(e.target.value))))}
              className="min-h-16 w-full rounded-md border-2 border-tinta bg-chapa text-center font-sinal text-4xl font-bold" />
            <button type="button" onClick={() => setQtd((q) => Math.min(t.qtd_solicitada, q + 1))} aria-label="Mais um"
              className="min-h-16 w-16 rounded-md border-2 border-tinta bg-chapa text-3xl font-bold">+</button>
          </div>
          <div className="mt-3 flex gap-2">
            <Botao variante="secundario" onClick={() => setEtapa("produto")}>Voltar</Botao>
            <Botao variante="confirmar" grande className="flex-1" disabled={qtd < 1} onClick={() => avancar("destino")}>Confirmar {fmtNum(qtd)}</Botao>
          </div>
          {qtd < t.qtd_solicitada && <p className="mt-2 font-semibold">O que faltar vira outra reposição.</p>}
        </section>
      ) : (
        <LeituraCodigo
          rotulo={etapa === "origem" ? "Bipe a posição de origem" : etapa === "produto" ? "Bipe o produto" : "Bipe a posição de destino"}
          onLeitura={leitura} desabilitado={ocupado} lampejo={lampejo} erro={tremor} />
      )}

      <div className="flex flex-wrap gap-2">
        {(etapa === "origem" || etapa === "produto") && <Botao variante="perigo" onClick={() => setConfirmar("falta")}>Falta na origem</Botao>}
        <Botao variante="secundario" onClick={() => setConfirmar("liberar")}>Devolver para a fila</Botao>
      </div>

      <Confirmar aberto={confirmar === "falta"} titulo={`Falta em ${t.origem}?`} rotuloConfirmar="Registrar falta" perigo ocupado={ocupado}
        onConfirmar={() => void executar("falta")} onCancelar={() => setConfirmar(null)}>
        A posição vai para inventário e o sistema procura outra origem para esta reposição.
      </Confirmar>
      <Confirmar aberto={confirmar === "liberar"} titulo="Devolver esta reposição para a fila?" rotuloConfirmar="Devolver" ocupado={ocupado}
        onConfirmar={() => void executar("liberar")} onCancelar={() => setConfirmar(null)}>
        Outra pessoa pode pegar. Se você já tirou o produto da origem, guarde no destino antes.
      </Confirmar>
    </div>
  );
}
